import { Breadcrumb } from "#/components/seo/Breadcrumb.js";
import { Tldr } from "#/components/seo/Tldr.js";
import { VerifiedAt } from "#/components/seo/VerifiedAt.js";
import { SourcesSection } from "#/components/seo/SourcesSection.js";
import type { CountryProjection, RegionList } from "#/server/data-fetchers.js";

type Props = {
  iso: string;
  projection: CountryProjection | null;
  regions: RegionList;
  locale: string;
};

export { Page };

function Page({ iso, projection, regions, locale }: Props) {
  const status = projection?.status ?? "unknown";
  const detail = projection?.detail ?? null;

  const sources = projection?.source_url
    ? [
        {
          url: projection.source_url,
          label: "Country status source",
          verifiedAt: projection.verified_at ?? undefined,
        },
      ]
    : [];

  return (
    <main id="main-content" className="container mx-auto ps-4 pe-4 py-6 max-w-3xl">
      <Breadcrumb
        items={[
          { name: "Home", href: "/" },
          { name: "Countries", href: "/" },
          { name: iso },
        ]}
        locale={locale}
      />

      <header className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">{iso}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Hantavirus surveillance — {status}
        </p>
        {projection?.verified_at ? (
          <div className="mt-2">
            <VerifiedAt isoTimestamp={projection.verified_at} locale={locale} />
          </div>
        ) : null}
      </header>

      {detail ? (
        <Tldr>{detail}</Tldr>
      ) : (
        <Tldr>
          No country-level commentary has been published yet. Regional case data
          is below; check back as the ingest pipeline picks up new reports from
          WHO, CDC, PAHO and ProMED.
        </Tldr>
      )}

      <section className="mt-6">
        <h2 className="text-xl font-semibold mb-3">Affected regions</h2>
        {regions.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No regional reports recorded for {iso} yet.
          </p>
        ) : (
          <ul className="space-y-3">
            {regions.map((region) => (
              <li
                key={region.id}
                className="border border-border rounded-md ps-3 pe-3 py-2"
              >
                <p className="font-medium">{region.name}</p>
                {region.dataText ? (
                  <p className="text-sm text-foreground/90 mt-1">{region.dataText}</p>
                ) : null}
                <p className="text-xs text-muted-foreground mt-1">
                  Scale: {region.scale}
                  {region.status ? ` · ${region.status}` : ""}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <SourcesSection sources={sources} locale={locale} />
    </main>
  );
}
